import type { SetStateFn } from '@/Types/react';
import useRelayEnvironmentStore from '@/Utility/hooks/useRelayEnvironmentStore';
import { createMediaItemMutation } from '@/graphql/mutations/Media.mutation';
import type { Media_CreateMediaItem_Mutation$data } from '@/graphql/mutations/Media.mutation';
import type { MimeTypeEnum } from '@/graphql/types';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button, LinearProgress, Modal, TextField, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import FilePreview from '../preview/FilePreview';

import './FileUploader.less';

export type FileUploaderAcceptType = 'image/*' | 'video/*' | 'audio/*' | 'image/*,video/*' | '*';

type QueryProps = {
  connections?: string[];
};

type SharedProps = {
  mimeTypes?: MimeTypeEnum[];
};

type FileUploaderProps = {
  file: File;
  setFile: SetStateFn<File | null>;
  callback?: (data: Media_CreateMediaItem_Mutation$data | null) => void;
};

export const FileUploaderWithPreview = ({
  file,
  setFile,
  callback = () => {},
  ...props
}: FileUploaderProps & QueryProps & SharedProps) => {
  const [title, setTitle] = useState(file.name);
  const [isLoading, setIsLoading] = useState(false);
  const [fileSupported, setFileSupported] = useState<boolean>(false);

  const { progress } = useRelayEnvironmentStore();

  const uploadHandler = () => {
    setIsLoading(true);
    createMediaItemMutation({ file, title }, props?.connections ?? [], (data, errors) => {
      setIsLoading(false);
      if (errors && errors.length > 0) {
        toast.error(errors.map((error) => error.message).join('\n'));
        return;
      }

      callback(data);
      setFile(null);
    });
  };

  const uploadButtonText = useMemo(() => {
    if (isLoading) return 'Uploading...';
    if (!fileSupported) return 'Choose a different file';
    return 'Upload';
  }, [isLoading, fileSupported]);

  const handleClear = () => {
    setFile(null);
  };

  return (
    <Modal open>
      <Box className="ZnglyModal Medium">
        <div className="UploadPreview">
          <Typography variant="h6">Preview</Typography>
          <FilePreview
            file={file}
            onClear={handleClear}
            setSupported={(isSupported) => {
              setFileSupported(isSupported);
            }}
            mimeTypes={props.mimeTypes}
          />
          {fileSupported && (
            <TextField
              id="FileNameInput"
              label="File Name"
              fullWidth
              sx={{ marginTop: '20px', paddingBottom: '20px' }}
              value={title}
              onKeyDown={({ key }) => {
                if (key === 'Enter') uploadHandler();
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setTitle(event.target.value);
              }}
            />
          )}
          <IconButton
            className="z-btn-5"
            type="submit"
            sx={{ width: '100%', opacity: isLoading ? 0.5 : 1, marginTop: 4 }}
            onClick={!fileSupported ? handleClear : uploadHandler}
            disabled={isLoading}
          >
            <FileUploadIcon />
            {uploadButtonText}
          </IconButton>
          {isLoading && <LinearProgress className="progress-bar" variant="determinate" value={progress} />}
        </div>
      </Box>
    </Modal>
  );
};

type Props = {
  onFileSelect: (file: File | null) => void;
  acceptType: FileUploaderAcceptType | string;
  customButton?: React.JSX.Element;
  disabled?: boolean;
};

const FileUploaderButton = ({ onFileSelect, acceptType, customButton, ...props }: Props & QueryProps & SharedProps) => {
  const [file, setFile] = useState<File | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // handle validations
    onFileSelect(e?.target?.files?.[0] ?? null);
    setFile(e?.target?.files?.[0] ?? null);
  };

  // clear inputRef when no file is selected
  useEffect(() => {
    if (!file && inputRef.current) inputRef.current.value = '';
  }, [file]);

  let buttonEl: React.JSX.Element;

  const inputElement = (
    <input ref={inputRef} type="file" accept={acceptType} onChange={handleFileInput} style={{ display: 'none' }} />
  );

  if (customButton) {
    buttonEl = (
      <>
        {inputElement} {customButton}
      </>
    );
    buttonEl = React.cloneElement(buttonEl, {
      onClick: () => {
        inputRef.current?.click();
      },
    });
  } else {
    buttonEl = (
      <Tooltip title="Upload File">
        <Button
          disabled={props?.disabled}
          startIcon={<FileUploadIcon />}
          className="z-btn-5"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          Upload
        </Button>
      </Tooltip>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept={acceptType} onChange={handleFileInput} style={{ display: 'none' }} />
      {buttonEl}
      {file && <FileUploaderWithPreview file={file} setFile={setFile} {...props} />}
    </>
  );
};

export default FileUploaderButton;
